import type { ReactNode } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { BikeIcon, CheckIcon, ClockIcon, FlameIcon, PackageIcon, PlusIcon, StarIcon, TruckIcon } from '../icons'
import { Avatar, Money, StatusBadge, StatusDot, Stepper, type ChowStatus } from './primitives'
import { KitchenBadge } from './KitchenBadge'
import { ExploreCard } from './ExploreCard'

/* ── shared bits ─────────────────────────────────────────────────────── */

const Bits = {
  Name: styled.h3`
    margin: 0;
    min-width: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    /* A card name is a heading at body size. bodySize would relabel it as
       body text, so the 16px stays fixed. */
    font-size: 1rem;
    line-height: 1.25;
    color: ${minimalVars.color.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  Meta: styled.p`
    margin: 0;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  Sep: styled.span`
    color: ${minimalVars.color.borderStrong};
  `,
  Facts: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    font-variant-numeric: tabular-nums;
    & > svg {
      flex-shrink: 0;
    }
  `,
}

/* ── ChefRow ─────────────────────────────────────────────────────────── */

/* Horizontal padded card used by ChefRow (and StoreRow). Dishes now use the
   image-forward `Card` above. */
const TableCard = styled.div`
  position: relative;
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  padding: 14px 16px;
  box-shadow: ${minimalVars.shadow.subtle};
  border-radius: ${minimalVars.radius.lg};
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease,
    background-color 180ms ease;

  &[data-hoverable] {
    cursor: pointer;
  }
  @media (hover: hover) and (pointer: fine) {
    &[data-hoverable]:hover {
      border-color: ${minimalVars.color.brand};
      background: ${minimalVars.color.bgElevated};
      transform: translateY(-2px);
      box-shadow: ${minimalVars.shadow.medium};
    }
  }
`

const ChefStyle = {
  Grid: styled.div`
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 14px;
    align-items: center;
  `,
  Main: styled.div`
    display: grid;
    gap: 4px;
    align-content: center;
    min-width: 0;
  `,
  NameWrap: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
  `,
  TitleRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
  `,
  Rating: styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    font-variant-numeric: tabular-nums;
    color: ${minimalVars.color.textPrimary};
    & > svg {
      color: ${minimalVars.color.warning};
    }
  `,
  Footer: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textSecondary};
  `,
  From: styled.span`
    color: ${minimalVars.color.textTertiary};
  `,
}

export interface ChefRowProps {
  name: string
  glyph: ReactNode
  cuisine: string
  area: string
  rating: number
  priceFrom: number
  status: ChowStatus
  /** Trending this week — renders a flame badge on the image corner. */
  hot?: boolean
  /** Server-derived verification tier; `none` renders nothing. */
  badge?: string
  onClick?: () => void
}

export const ChefRow = ({
  name,
  glyph,
  cuisine,
  area,
  rating,
  priceFrom,
  status,
  hot = false,
  badge,
  onClick,
}: ChefRowProps) => (
  <TableCard data-hoverable={Boolean(onClick) || undefined} onClick={onClick}>
    <ChefStyle.Grid>
      <Avatar
        glyph={glyph}
        size={64}
        badge={hot ? <FlameIcon size="sm" aria-label="Trending this week" /> : undefined}
      />
      <ChefStyle.Main>
        <ChefStyle.TitleRow>
          <ChefStyle.NameWrap>
            <Bits.Name>{name}</Bits.Name>
            {badge ? <KitchenBadge tier={badge} /> : null}
          </ChefStyle.NameWrap>
          <ChefStyle.Rating aria-label={`Rated ${rating.toFixed(1)} out of 5`}>
            <StarIcon size="sm" aria-hidden /> {rating.toFixed(1)}
          </ChefStyle.Rating>
        </ChefStyle.TitleRow>
        <Bits.Meta>
          {cuisine} · {area}
        </Bits.Meta>
        <ChefStyle.Footer>
          <StatusDot status={status} />
          <Bits.Sep aria-hidden>·</Bits.Sep>
          <ChefStyle.From>from</ChefStyle.From>
          <Money amount={priceFrom} tone="brand" size="sm" />
        </ChefStyle.Footer>
      </ChefStyle.Main>
    </ChefStyle.Grid>
  </TableCard>
)

/* ── DishRow ─────────────────────────────────────────────────────────── */

/* ── DishRow — the shared explore shell, with a dish's controls ──────────
   This card used to declare its own surface, media box, heading and hover
   motion. Those are exactly the things the explore shell exists to own, so the
   dish was the one card that looked different on the same shelf: a different
   media ratio, a taller minimum, a two-line name. It now composes `ExploreCard`
   and contributes only what is genuinely a dish — the rating and stock chips,
   the favourite toggle, and the quantity stepper with its add button.

   The controls sit in the shell's `action` slot, OUTSIDE the link. A link
   wrapping a button is invalid HTML and swallows the click, and the star toggle
   additionally stops propagation so favouriting never navigates. */

const DishStyle = {
  /* Holds whatever ChowThumb renders — a photo <img> cover-fills; an
     illustration/glyph is centred. Isolated from the overlay chips so their
     absolute positioning is never clobbered. */
  Fill: styled.div`
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;

    & > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    & > svg {
      width: 100%;
      height: 100%;
    }
    & > span {
      width: 100%;
      height: 100%;
      display: grid;
      place-items: center;
    }
  `,
  /* The scrim and the chips live in one overlay layer so the shell's media box
     stays a plain reserved rectangle and the dish decides what sits on it. */
  Overlay: styled.div`
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;

    & > * {
      pointer-events: auto;
    }
  `,
  RatingChip: styled.span`
    position: absolute;
    left: 10px;
    bottom: 10px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 4px 9px;
    border-radius: ${minimalVars.radius.pill};
    background: ${minimalVars.color.success};
    color: #fff;
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    font-variant-numeric: tabular-nums;
    box-shadow: 0 2px 8px -2px rgba(20, 10, 5, 0.4);
    & > svg {
      color: #fff;
    }
  `,
  CartChip: styled.span`
    position: absolute;
    left: 10px;
    top: 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 9px 4px 7px;
    border-radius: ${minimalVars.radius.pill};
    background: ${minimalVars.color.brand};
    color: #fff;
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    box-shadow: 0 2px 8px -2px rgba(20, 10, 5, 0.4);
  `,
  SoldOutTag: styled.span`
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 4px 10px;
    border-radius: ${minimalVars.radius.pill};
    background: rgba(20, 10, 5, 0.72);
    color: #fff;
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  `,
  Star: styled.button<{ $on: boolean }>`
    position: absolute;
    top: 8px;
    right: 8px;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: ${minimalVars.radius.pill};
    background: var(--chow-glass-light, rgba(255, 255, 255, 0.92));
    backdrop-filter: var(--chow-backdrop-filter, blur(4px));
    cursor: pointer;
    color: ${({ $on }) => ($on ? minimalVars.color.brand : minimalVars.color.textSecondary)};
    box-shadow: 0 2px 8px -2px rgba(20, 10, 5, 0.35);
    transition: color 160ms ease, transform 160ms ease;

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 2px;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: ${minimalVars.color.brand};
        transform: scale(1.06);
      }
    }
  `,
  ChefMeta: styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
    min-width: 0;
    & > svg {
      flex-shrink: 0;
    }
  `,
  Facts: styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 10px;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    font-variant-numeric: tabular-nums;
  `,
  Fact: styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    & > svg {
      flex-shrink: 0;
      color: ${minimalVars.color.textTertiary};
    }
  `,
  Footer: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
  `,
  Controls: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 599px) {
      gap: 6px;
    }
  `,
  Price: styled.span<{ $soldOut?: boolean }>`
    flex-shrink: 0;
    opacity: ${({ $soldOut }) => ($soldOut ? 0.7 : 1)};
  `,
}

/**
 * The add control.
 *
 * It lives in the shell's action slot, outside the link, so the click adds to the
 * cart and never navigates. A link wrapping a button is invalid HTML and the
 * button swallows the click.
 */
const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 38px;
  height: 38px;
  padding: 0 16px;
  border-radius: ${minimalVars.radius.pill};
  font-family: ${minimalVars.typography.displayFamily};
  font-size: ${minimalVars.typography.captionSize};
  font-weight: ${minimalVars.typography.weightBold};
  letter-spacing: 0.01em;
  border: 0;
  cursor: pointer;
  background: ${minimalVars.color.brand};
  color: ${minimalVars.color.textInverse};
  box-shadow: 0 4px 12px -3px color-mix(in srgb, ${minimalVars.color.brand} 40%, transparent);
  transition: filter 140ms ease, transform 140ms ease, box-shadow 140ms ease;
  width: 100%;
  flex: 1 1 auto;

  & > svg {
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    filter: brightness(1.06);
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${minimalVars.color.brandSoft};
    color: ${minimalVars.color.brand};
    box-shadow: none;
    cursor: default;
  }

  /* 599px matches no named stop. until('page') is 768px, so the button width
     would change on a different width. */
  @media (max-width: 599px) {
    min-height: 34px;
    height: 34px;
    padding: 0 12px;
    /* The wide rule already sets captionSize, so the phone step keeps it. */
    font-size: ${minimalVars.typography.captionSize};
    flex: 1;
    max-width: none;
  }
`

export interface DishRowProps {
  name: string
  chef: string
  chefGlyph?: ReactNode
  glyph?: ReactNode
  price: number
  qty: number
  onQty: (value: number) => void
  onAdd?: () => void
  /**
   * Destination of the dish's detail page. Present makes the cover and the name a
   * real router link, which navigates in-app; a raw anchor would reload the whole
   * application and throw away the transition. Absent renders no link at all
   * rather than a div pretending to be one.
   */
  to?: string
  /** Render a star toggle. Omit onFavourite to hide it entirely (chef views). */
  onFavourite?: () => void
  favourite?: boolean
  addedToCart?: boolean
  /** True while the add command is in flight; disables the button and shows progress. */
  pending?: boolean
  stock?: number
  status?: string
  /** Optional star rating shown as a chip on the cover (e.g. 4.6). */
  rating?: number
  /** Optional delivery/prep estimate shown in the facts row (e.g. "25–35 min"). */
  etaLabel?: string
}

export const DishRow = ({
  name,
  chef,
  chefGlyph,
  glyph,
  price,
  qty,
  onQty,
  onAdd,
  to,
  onFavourite,
  favourite = false,
  addedToCart = false,
  pending = false,
  stock,
  status = 'available',
  rating,
  etaLabel,
}: DishRowProps) => {
  const isSoldOut = status === 'sold_out' || stock === 0
  const isButtonDisabled = isSoldOut || pending
  const stockLabel =
    stock === undefined
      ? null
      : isSoldOut
        ? 'Sold out'
        : stock <= 5
          ? `Only ${stock} left`
          : 'In stock'
  const stockTone = isSoldOut ? '#e11d48' : stock !== undefined && stock <= 5 ? '#f59e0b' : '#10b981'

  // The overlay is one layer so the chips can position against the media box
  // without each needing its own stacking context.
  const overlay = (
    <DishStyle.Overlay>
      {rating !== undefined && !isSoldOut && (
        <DishStyle.RatingChip>
          <StarIcon size="sm" fill="currentColor" />
          {rating.toFixed(1)}
        </DishStyle.RatingChip>
      )}
      {addedToCart && !isSoldOut && (
        <DishStyle.CartChip aria-label="In cart">
          <CheckIcon size="sm" />
          In cart
        </DishStyle.CartChip>
      )}
      {isSoldOut && <DishStyle.SoldOutTag>Sold out</DishStyle.SoldOutTag>}
      {onFavourite && (
        <DishStyle.Star
          type="button"
          $on={favourite}
          aria-pressed={favourite}
          aria-label={favourite ? `Remove ${name} from favourites` : `Save ${name} to favourites`}
          onClick={(event) => {
            // The star sits over the cover the link covers. Stop the click so
            // favouriting never also navigates into the dish.
            event.stopPropagation()
            event.preventDefault()
            onFavourite()
          }}
        >
          <StarIcon size="sm" fill={favourite ? 'currentColor' : 'none'} />
        </DishStyle.Star>
      )}
    </DishStyle.Overlay>
  )

  return (
    <ExploreCard
      to={to}
      soldOut={isSoldOut}
      media={<DishStyle.Fill>{glyph}</DishStyle.Fill>}
      mediaOverlay={overlay}
      title={name}
      subtitle={<DishStyle.ChefMeta>{chefGlyph} by {chef}</DishStyle.ChefMeta>}
      meta={
        etaLabel || stockLabel ? (
          <DishStyle.Facts>
            {etaLabel && (
              <DishStyle.Fact>
                <ClockIcon size="sm" />
                {etaLabel}
              </DishStyle.Fact>
            )}
            {etaLabel && (
              <DishStyle.Fact>
                <BikeIcon size="sm" />
                Kitchen pickup
              </DishStyle.Fact>
            )}
            {stockLabel && (
              <DishStyle.Fact style={{ color: stockTone, fontWeight: 600 }}>{stockLabel}</DishStyle.Fact>
            )}
          </DishStyle.Facts>
        ) : null
      }
      action={
        // The shell replaces its footer with the action slot, so the price, the
        // stepper and the add button share one row here rather than being split
        // between a footer the shell would no longer render. The price stays even
        // where there is no add control, because a chef still reads the price.
        <DishStyle.Footer>
          <DishStyle.Price $soldOut={isSoldOut}>
            <Money amount={price} tone="brand" size="md" />
          </DishStyle.Price>
          {onAdd ? (
            <DishStyle.Controls>
              <Stepper value={qty} onChange={onQty} min={1} max={stock} disabled={isButtonDisabled} />
              <ActionButton disabled={isButtonDisabled} aria-busy={pending} onClick={onAdd}>
                {pending ? (
                  'Adding…'
                ) : isSoldOut ? (
                  'Sold out'
                ) : (
                  <>
                    <PlusIcon size="sm" />
                    {addedToCart ? 'Add more' : 'Add'}
                  </>
                )}
              </ActionButton>
            </DishStyle.Controls>
          ) : null}
        </DishStyle.Footer>
      }
    />
  )
}

/* ── StoreRow ────────────────────────────────────────────────────────── */

export type StorePalette = 'red' | 'green' | 'orange'

const storeGradient: Record<StorePalette, string> = {
  red: '#5c1914',
  green: '#134f37',
  orange: '#8c3e12',
}

const StoreStyle = {
  Card: styled.article`
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid ${minimalVars.color.borderSubtle};
    background: ${minimalVars.color.bgSurface};
    box-shadow: ${minimalVars.shadow.subtle};
  `,
  Head: styled.div<{ $gradient: string }>`
    background: ${({ $gradient }) => $gradient};
    color: ${minimalVars.color.textInverse};
    padding: ${minimalVars.space.sm};
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  HeadCopy: styled.div`
    display: grid;
    gap: 3px;
    min-width: 0;
  `,
  Category: styled.span`
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightBold};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.78);
  `,
  StoreName: styled.h3`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    /* The store name keeps a nowrap ellipsis column beside the status badge, so h2Size would cut names short. */
    font-size: 1.25rem;
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  StoreArea: styled.span`
    font-size: ${minimalVars.typography.captionSize};
    color: rgba(255, 255, 255, 0.78);
  `,
  HeadSide: styled.div`
    display: grid;
    justify-items: end;
    gap: 4px;
    text-align: right;
  `,
  Hours: styled.span`
    font-size: ${minimalVars.typography.metaSize};
    color: rgba(255, 255, 255, 0.78);
  `,
  Foot: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.sm};
    padding: ${minimalVars.space.xs} ${minimalVars.space.sm};
  `,
}

export interface StoreRowProps {
  category: string
  name: ReactNode
  area: string
  status: ChowStatus
  hours: string
  rating: number
  etaMins: string
  items: number
  minOrder: number
  palette?: StorePalette
  onClick?: () => void
}

export const StoreRow = ({
  category,
  name,
  area,
  status,
  hours,
  rating,
  etaMins,
  items,
  minOrder,
  palette = 'red',
  onClick,
}: StoreRowProps) => (
  <StoreStyle.Card onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
    <StoreStyle.Head $gradient={storeGradient[palette]}>
      <StoreStyle.HeadCopy>
        <StoreStyle.Category>{category}</StoreStyle.Category>
        <StoreStyle.StoreName>{name}</StoreStyle.StoreName>
        <StoreStyle.StoreArea>{area}</StoreStyle.StoreArea>
      </StoreStyle.HeadCopy>
      <StoreStyle.HeadSide>
        <StatusBadge status={status} />
        <StoreStyle.Hours>{hours}</StoreStyle.Hours>
      </StoreStyle.HeadSide>
    </StoreStyle.Head>
    <StoreStyle.Foot>
      <Bits.Facts>
        <StarIcon size="sm" /> {rating.toFixed(1)}
        <span aria-hidden>·</span>
        <TruckIcon size="sm" /> {etaMins}
        <span aria-hidden>·</span>
        <PackageIcon size="sm" /> {items}
      </Bits.Facts>
      <Money amount={minOrder} tone="brand" suffix=" min →" size="sm" />
    </StoreStyle.Foot>
  </StoreStyle.Card>
)
